import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail, resolveImg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CITIES, DISCIPLINES, STANCES, WORK_TYPES, DISCIPLINE_COLORS } from "@/lib/constants";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  Crosshair, LogOut, Users, UserPlus, Dumbbell, MapPin, Flame, ChevronLeft,
  Plus, X, Swords, Wind, Hand, Check, Clock, Search, Calendar, CalendarClock,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import TrainerProfilePanel from "@/components/TrainerProfilePanel";
import SessionsView from "@/components/SessionsView";

const CAMP_STATUS = {
  planning: { label: "Planning", cls: "text-yellow-400 border-yellow-400/40 bg-yellow-400/5" },
  active: { label: "Active", cls: "text-volt border-volt/40 bg-volt/10" },
  complete: { label: "Complete", cls: "text-zinc-400 border-zinc-500/40 bg-zinc-500/5" },
};

function fmtRange(s, e) {
  if (!s && !e) return null;
  const f = (d) => new Date(d + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  if (s && e) return `${f(s)} – ${f(e)}`;
  return f(s || e);
}

const selCls = "bg-[#0d0d0d] border-[#27272a] text-white h-10 focus:ring-2 focus:ring-volt";
const WORK_ICON = { "Hard Sparring": Swords, "Technical/Flow Sparring": Wind, "Padwork/Clinch": Hand };

// ---------------- Add-to-stable modal
function AddFighterModal({ open, onOpenChange, onInvited }) {
  const [filters, setFilters] = useState({ city: "All", discipline: "All", stance: "All" });
  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
  const feed = useQuery({
    queryKey: ["trainer-search", filters, open],
    enabled: open,
    queryFn: async () => {
      const params = {};
      if (filters.city !== "All") params.city = filters.city;
      if (filters.discipline !== "All") params.discipline = filters.discipline;
      if (filters.stance !== "All") params.stance = filters.stance;
      return (await api.get("/fighters", { params })).data.fighters;
    },
  });
  const invite = async (f) => {
    try {
      await api.post("/stable/invite", { fighter_id: f.id });
      toast.success(`Invite sent to ${f.fighter_name}.`);
      onInvited();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-fighter-modal" className="bg-[#161616] border-[#27272a] text-white max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl uppercase tracking-tight">Add fighter to stable</DialogTitle>
          <DialogDescription className="text-zinc-400">Send a join request — the fighter must accept.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-3">
          <SelectFilter value={filters.city} onChange={set("city")} options={CITIES} ph="Regions" />
          <SelectFilter value={filters.discipline} onChange={set("discipline")} options={DISCIPLINES} ph="Disciplines" />
          <SelectFilter value={filters.stance} onChange={set("stance")} options={STANCES} ph="Stances" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mt-2">
          {(feed.data || []).map((f) => (
            <FighterMini key={f.id} f={f} action={() => invite(f)} actionLabel="Invite" testid={`invite-fighter-${f.id}`} />
          ))}
          {feed.data && feed.data.length === 0 && (
            <p className="text-sm text-zinc-500 col-span-2 text-center py-6">No fighters match.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SelectFilter({ value, onChange, options, ph }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={`${selCls} min-w-[130px]`}><SelectValue placeholder={ph} /></SelectTrigger>
      <SelectContent className="bg-[#161616] border-[#27272a] text-white">
        <SelectItem value="All" className="focus:bg-volt focus:text-black">All {ph}</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o} className="focus:bg-volt focus:text-black">{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function FighterMini({ f, action, actionLabel, testid, disabled }) {
  const dColor = DISCIPLINE_COLORS[f.discipline] || "#00FF66";
  return (
    <div className="flex items-center gap-3 p-3 bg-[#0d0d0d] border border-[#27272a] rounded-xl">
      <div className="w-12 h-12 rounded-lg overflow-hidden bg-[#161616] flex items-center justify-center shrink-0">
        {f.avatar_url ? <img src={resolveImg(f.avatar_url)} alt="" className="w-full h-full object-cover" />
          : <span className="font-heading text-xl text-zinc-700">{f.fighter_name?.[0]}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading uppercase tracking-wide truncate">{f.fighter_name}</div>
        <div className="text-xs text-zinc-500 truncate">{f.gym_name} · {f.city}</div>
        <div className="flex gap-1.5 mt-1">
          <span className="text-[10px] uppercase px-1.5 py-0.5 rounded" style={{ background: dColor, color: "#000" }}>{f.discipline}</span>
          <span className="text-[10px] uppercase text-zinc-400 border border-[#27272a] px-1.5 py-0.5 rounded">{f.weight}{f.weight_unit}</span>
        </div>
      </div>
      <button
        data-testid={testid}
        onClick={action}
        disabled={disabled}
        className="shrink-0 bg-volt text-black text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-lg hover:bg-volt-hover transition-colors duration-200 disabled:opacity-40"
      >
        {actionLabel}
      </button>
    </div>
  );
}

// ---------------- Camp schedule editor
function ScheduleEditor({ camp, fighterId }) {
  const qc = useQueryClient();
  const [start, setStart] = useState(camp.start_date || "");
  const [end, setEnd] = useState(camp.end_date || "");
  const [status, setStatus] = useState(camp.status || "planning");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setStart(camp.start_date || "");
    setEnd(camp.end_date || "");
    setStatus(camp.status || "planning");
  }, [camp.id]); // eslint-disable-line

  const save = async () => {
    if (start && end && end < start) { toast.error("End date can't be before start date."); return; }
    setSaving(true);
    try {
      await api.patch(`/camps/${camp.id}`, { start_date: start, end_date: end, status });
      qc.invalidateQueries({ queryKey: ["camps", fighterId] });
      toast.success("Schedule saved.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const dateCls = "bg-[#0d0d0d] border border-[#27272a] text-white rounded-lg h-10 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-volt [color-scheme:dark] w-full";

  return (
    <div className="bg-card border border-white/10 rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-3"><Calendar size={15} className="text-volt" /><span className="text-xs uppercase tracking-widest text-zinc-400">Schedule</span></div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <label className="text-xs uppercase tracking-wider text-zinc-500">Start</label>
          <input data-testid="camp-start-date" type="date" value={start} onChange={(e) => setStart(e.target.value)} className={dateCls} />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs uppercase tracking-wider text-zinc-500">End</label>
          <input data-testid="camp-end-date" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className={dateCls} />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs uppercase tracking-wider text-zinc-500">Status</label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="camp-status" className={selCls}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#161616] border-[#27272a] text-white">
              {Object.entries(CAMP_STATUS).map(([k, v]) => <SelectItem key={k} value={k} className="focus:bg-volt focus:text-black">{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <button data-testid="save-schedule-btn" onClick={save} disabled={saving} className="mt-4 bg-volt text-black font-heading font-bold uppercase tracking-widest text-sm px-5 py-2.5 rounded-xl hover:bg-volt-hover transition-colors duration-200 disabled:opacity-40">
        {saving ? "Saving…" : "Save Schedule"}
      </button>
    </div>
  );
}

// ---------------- Camp builder
function CampBuilder({ fighter, onBack }) {
  const qc = useQueryClient();
  const [newCampName, setNewCampName] = useState("");
  const [activeCampId, setActiveCampId] = useState(null);
  const [prospectFilters, setProspectFilters] = useState({ city: "All", discipline: "All", stance: "All" });
  const [workType, setWorkType] = useState("Technical/Flow Sparring");
  const [prospectWhen, setProspectWhen] = useState("");
  const pset = (k) => (v) => setProspectFilters((f) => ({ ...f, [k]: v }));

  const campsQ = useQuery({
    queryKey: ["camps", fighter.id],
    queryFn: async () => {
      const data = (await api.get("/camps", { params: { fighter_id: fighter.id } })).data.camps;
      if (data.length && !activeCampId) setActiveCampId(data[0].id);
      return data;
    },
  });
  const camps = campsQ.data || [];
  const activeCamp = camps.find((c) => c.id === activeCampId) || null;

  const search = useQuery({
    queryKey: ["prospect-search", prospectFilters, activeCampId],
    enabled: !!activeCamp,
    queryFn: async () => {
      const params = {};
      if (prospectFilters.city !== "All") params.city = prospectFilters.city;
      if (prospectFilters.discipline !== "All") params.discipline = prospectFilters.discipline;
      if (prospectFilters.stance !== "All") params.stance = prospectFilters.stance;
      return (await api.get("/fighters", { params })).data.fighters;
    },
  });

  const createCamp = async () => {
    if (!newCampName.trim()) { toast.error("Name the camp."); return; }
    try {
      const { data } = await api.post("/camps", { fighter_id: fighter.id, name: newCampName.trim() });
      setNewCampName("");
      setActiveCampId(data.camp.id);
      qc.invalidateQueries({ queryKey: ["camps", fighter.id] });
      toast.success("Camp created.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addProspect = async (f) => {
    try {
      await api.post(`/camps/${activeCamp.id}/prospects`, { prospect_id: f.id, work_type: workType, scheduled_at: prospectWhen || null });
      toast.success(`${f.fighter_name} invited to the camp.`);
      qc.invalidateQueries({ queryKey: ["camps", fighter.id] });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const removeProspect = async (inviteId) => {
    try {
      await api.delete(`/camps/${activeCamp.id}/prospects/${inviteId}`);
      qc.invalidateQueries({ queryKey: ["camps", fighter.id] });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const prospectIds = new Set((activeCamp?.prospects || []).map((p) => p.id));
  const dColor = DISCIPLINE_COLORS[fighter.discipline] || "#00FF66";

  return (
    <div>
      <button data-testid="camp-back-btn" onClick={onBack} className="flex items-center gap-1.5 text-zinc-400 hover:text-volt transition-colors duration-200 text-sm uppercase tracking-wider mb-4">
        <ChevronLeft size={16} /> Back to stable
      </button>

      <div className="grid lg:grid-cols-[340px_1fr] gap-6">
        {/* Left: fighter + camps */}
        <div className="space-y-4">
          <div className="bg-card border border-white/10 rounded-2xl overflow-hidden">
            <div className="h-40 relative">
              {fighter.avatar_url ? <img src={resolveImg(fighter.avatar_url)} alt="" className="w-full h-full object-cover object-top" />
                : <div className="w-full h-full bg-[#0d0d0d] flex items-center justify-center"><span className="font-heading text-5xl text-zinc-800">{fighter.fighter_name?.[0]}</span></div>}
              <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
              <span className="absolute bottom-3 left-4 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" style={{ background: dColor, color: "#000" }}>{fighter.discipline}</span>
            </div>
            <div className="p-4">
              <h2 className="font-heading text-2xl uppercase tracking-tight">{fighter.fighter_name}</h2>
              <div className="flex items-center gap-1.5 text-sm text-zinc-400 mt-1"><Dumbbell size={13} className="text-volt" />{fighter.gym_name}</div>
              <div className="flex items-center gap-1.5 text-xs text-zinc-500 mt-1"><MapPin size={12} />{fighter.city} · {fighter.stance} · {fighter.weight}{fighter.weight_unit}</div>
            </div>
          </div>

          <div className="bg-card border border-white/10 rounded-2xl p-4">
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Camps</p>
            <div className="space-y-2">
              {camps.map((c) => (
                <button
                  key={c.id}
                  data-testid={`camp-tab-${c.id}`}
                  onClick={() => setActiveCampId(c.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg border transition-colors duration-200 ${
                    activeCampId === c.id ? "border-volt bg-volt/10 text-white" : "border-[#27272a] text-zinc-400 hover:text-white"
                  }`}
                >
                  <div className="font-heading uppercase tracking-wide text-sm">{c.name}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${(CAMP_STATUS[c.status] || CAMP_STATUS.planning).cls}`}>{(CAMP_STATUS[c.status] || CAMP_STATUS.planning).label}</span>
                    <span className="text-[11px] text-zinc-500">{c.prospects.length}/4</span>
                  </div>
                  {fmtRange(c.start_date, c.end_date) && <div className="text-[11px] text-zinc-500 mt-0.5 flex items-center gap-1"><Calendar size={10} />{fmtRange(c.start_date, c.end_date)}</div>}
                </button>
              ))}
              {camps.length === 0 && <p className="text-xs text-zinc-600">No camps yet. Create one below.</p>}
            </div>
            <div className="flex gap-2 mt-3">
              <Input data-testid="new-camp-name" value={newCampName} onChange={(e) => setNewCampName(e.target.value)} placeholder="New camp name" className="bg-[#0d0d0d] border-[#27272a] text-white h-10" />
              <button data-testid="create-camp-btn" onClick={createCamp} className="shrink-0 bg-volt text-black px-3 rounded-lg hover:bg-volt-hover transition-colors duration-200"><Plus size={18} /></button>
            </div>
          </div>
        </div>

        {/* Right: camp detail */}
        <div>
          {!activeCamp ? (
            <div className="h-full flex items-center justify-center border border-dashed border-[#27272a] rounded-2xl py-20 text-center">
              <div>
                <Flame className="mx-auto text-zinc-700 mb-2" size={28} />
                <p className="font-heading uppercase tracking-wide text-zinc-400">Create a camp to start building</p>
                <p className="text-sm text-zinc-600 mt-1">Add up to 4 sparring partners for {fighter.fighter_name}.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <ScheduleEditor camp={activeCamp} fighterId={fighter.id} />
              {/* prospects */}
              <div className="bg-card border border-white/10 rounded-2xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading text-xl uppercase tracking-tight">{activeCamp.name} · Sparring Partners</h3>
                  <span className="text-xs uppercase tracking-wider text-volt">{activeCamp.prospects.length}/4</span>
                </div>
                {activeCamp.prospects.length === 0 ? (
                  <p className="text-sm text-zinc-600">No partners yet — add prospects from the pool below.</p>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-3">
                    {activeCamp.prospects.map((p) => {
                      const WI = WORK_ICON[p.work_type] || Swords;
                      return (
                        <div key={p.invite_id} data-testid={`prospect-${p.id}`} className="flex items-center gap-3 p-3 bg-[#0d0d0d] border border-[#27272a] rounded-xl">
                          <div className="w-10 h-10 rounded-lg overflow-hidden bg-[#161616] flex items-center justify-center shrink-0">
                            {p.avatar_url ? <img src={resolveImg(p.avatar_url)} alt="" className="w-full h-full object-cover" /> : <span className="font-heading text-zinc-700">{p.fighter_name?.[0]}</span>}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-heading uppercase tracking-wide text-sm truncate">{p.fighter_name}</div>
                            <div className="flex items-center gap-1 text-[11px] text-zinc-500"><WI size={11} />{p.work_type}</div>
                            <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider mt-1 px-1.5 py-0.5 rounded ${
                              p.prospect_status === "accepted" ? "text-volt border border-volt/40" : "text-yellow-400 border border-yellow-400/40"
                            }`}>
                              {p.prospect_status === "accepted" ? <Check size={9} /> : <Clock size={9} />}{p.prospect_status}
                            </span>
                          </div>
                          <button data-testid={`remove-prospect-${p.id}`} onClick={() => removeProspect(p.invite_id)} className="text-zinc-500 hover:text-[#FF3B30] transition-colors duration-200"><X size={16} /></button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* add pool */}
              {activeCamp.prospects.length < 4 && (
                <div className="bg-card border border-white/10 rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-3"><Search size={15} className="text-volt" /><span className="text-xs uppercase tracking-widest text-zinc-400">Find sparring partners</span></div>
                  <div className="flex flex-wrap gap-3 items-center mb-4">
                    <SelectFilter value={prospectFilters.city} onChange={pset("city")} options={CITIES} ph="Regions" />
                    <SelectFilter value={prospectFilters.discipline} onChange={pset("discipline")} options={DISCIPLINES} ph="Disciplines" />
                    <SelectFilter value={prospectFilters.stance} onChange={pset("stance")} options={STANCES} ph="Stances" />
                    <Select value={workType} onValueChange={setWorkType}>
                      <SelectTrigger className={`${selCls} min-w-[180px]`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#161616] border-[#27272a] text-white">
                        {WORK_TYPES.map((w) => <SelectItem key={w} value={w} className="focus:bg-volt focus:text-black">{w}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <input data-testid="prospect-schedule" type="datetime-local" value={prospectWhen} onChange={(e) => setProspectWhen(e.target.value)} className="bg-[#0d0d0d] border border-[#27272a] text-white rounded-lg h-10 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-volt [color-scheme:dark]" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3 max-h-[420px] overflow-y-auto">
                    {(search.data || []).filter((f) => f.id !== fighter.id && !prospectIds.has(f.id)).map((f) => (
                      <FighterMini key={f.id} f={f} action={() => addProspect(f)} actionLabel="Add" testid={`add-prospect-${f.id}`} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------- Dashboard shell
export default function TrainerDashboard() {
  const { user, logout } = useAuth();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [dashView, setDashView] = useState("stable");

  const stableQ = useQuery({
    queryKey: ["stable"],
    queryFn: async () => (await api.get("/stable")).data,
  });
  const stable = stableQ.data?.stable || [];
  const pending = stableQ.data?.pending || [];

  const refresh = () => qc.invalidateQueries({ queryKey: ["stable"] });

  return (
    <div className="min-h-screen relative z-10">
      <header className="sticky top-0 z-40 bg-[#0d0d0d]/95 backdrop-blur border-b border-[#27272a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Crosshair className="text-volt" size={24} strokeWidth={2.5} />
            <span className="font-heading font-black text-xl uppercase tracking-tight hidden sm:block">Spar<span className="text-volt">Base</span></span>
            <span className="ml-2 text-[10px] uppercase tracking-widest bg-volt/15 text-volt border border-volt/30 px-2 py-0.5 rounded">Trainer</span>
          </div>
          <nav className="flex items-center gap-1 bg-[#161616] border border-[#27272a] rounded-sm p-1">
            <button data-testid="nav-stable" onClick={() => { setDashView("stable"); setSelected(null); }} className={`flex items-center gap-2 px-4 py-2 rounded-sm font-heading uppercase tracking-wider text-sm transition-colors duration-200 ${dashView === "stable" ? "bg-volt text-black font-bold" : "text-zinc-400 hover:text-white"}`}><Users size={16} /><span className="hidden sm:inline">Stable</span></button>
            <button data-testid="nav-sessions" onClick={() => setDashView("sessions")} className={`flex items-center gap-2 px-4 py-2 rounded-sm font-heading uppercase tracking-wider text-sm transition-colors duration-200 ${dashView === "sessions" ? "bg-volt text-black font-bold" : "text-zinc-400 hover:text-white"}`}><CalendarClock size={16} /><span className="hidden sm:inline">Sessions</span></button>
          </nav>
          <div className="flex items-center gap-3">
            <button data-testid="open-trainer-profile-btn" onClick={() => setProfileOpen(true)} className="flex items-center gap-3 group">
              <div className="w-9 h-9 rounded-xl overflow-hidden border border-[#27272a] bg-[#0d0d0d] flex items-center justify-center group-hover:border-volt transition-colors duration-200">
                {user?.avatar_url ? <img src={resolveImg(user.avatar_url)} alt="" className="w-full h-full object-cover" /> : <span className="font-heading text-zinc-500">{user?.name?.[0] || "?"}</span>}
              </div>
              <div className="text-right hidden lg:block">
                <div className="font-heading uppercase text-sm leading-none group-hover:text-volt transition-colors duration-200">{user?.name}</div>
                <div className="text-[11px] text-zinc-500">{user?.gym_name}</div>
              </div>
            </button>
            <button data-testid="logout-btn" onClick={logout} className="w-9 h-9 flex items-center justify-center border border-[#27272a] rounded-xl text-zinc-400 hover:text-[#FF3B30] hover:border-[#FF3B30] transition-colors duration-200"><LogOut size={16} /></button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {dashView === "sessions" ? (
          <SessionsView />
        ) : (
        <AnimatePresence mode="wait">
          {selected ? (
            <motion.div key="builder" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
              <CampBuilder fighter={selected} onBack={() => setSelected(null)} />
            </motion.div>
          ) : (
            <motion.div key="stable" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="flex items-center gap-2 text-volt"><Users size={18} /><span className="text-xs uppercase tracking-[0.3em]">My Stable</span></div>
                  <h1 className="font-heading font-extrabold uppercase text-4xl sm:text-5xl tracking-tight mt-1">{stable.length} Fighter{stable.length === 1 ? "" : "s"}<span className="text-volt">.</span></h1>
                </div>
                <button data-testid="add-fighter-btn" onClick={() => setAddOpen(true)} className="flex items-center gap-2 bg-volt text-black font-heading font-bold uppercase tracking-widest text-sm px-5 py-3 rounded-xl hover:bg-volt-hover transition-colors duration-200">
                  <UserPlus size={16} /> Add Fighter
                </button>
              </div>

              {pending.length > 0 && (
                <div className="mb-6">
                  <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Pending invites</p>
                  <div className="flex flex-wrap gap-2">
                    {pending.map((p) => (
                      <span key={p.id} className="flex items-center gap-2 text-sm border border-yellow-400/30 text-yellow-400 bg-yellow-400/5 px-3 py-1.5 rounded-lg">
                        <Clock size={13} /> {p.to_name} · awaiting reply
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {stable.length === 0 ? (
                <div className="text-center py-20 border border-dashed border-[#27272a] rounded-2xl">
                  <Users size={32} className="mx-auto text-zinc-700 mb-3" />
                  <p className="font-heading text-xl uppercase tracking-wide text-zinc-400">Your stable is empty</p>
                  <p className="text-sm text-zinc-600 mt-1">Invite fighters to start building camps.</p>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="stable-grid">
                  {stable.map((f) => {
                    const dColor = DISCIPLINE_COLORS[f.discipline] || "#00FF66";
                    return (
                      <button
                        key={f.id}
                        data-testid={`stable-fighter-${f.id}`}
                        onClick={() => setSelected(f)}
                        className="text-left bg-card border border-white/10 rounded-2xl overflow-hidden hover:border-volt/50 hover:-translate-y-1 transition-transform transition-colors duration-200 group"
                      >
                        <div className="h-44 relative overflow-hidden">
                          {f.avatar_url ? <img src={resolveImg(f.avatar_url)} alt="" className="w-full h-full object-cover object-top grayscale-[30%] group-hover:grayscale-0 transition-all duration-300" />
                            : <div className="w-full h-full bg-[#0d0d0d] flex items-center justify-center"><span className="font-heading text-6xl text-zinc-800">{f.fighter_name?.[0]}</span></div>}
                          <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
                          <span className="absolute bottom-3 left-4 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" style={{ background: dColor, color: "#000" }}>{f.discipline}</span>
                        </div>
                        <div className="p-4">
                          <h3 className="font-heading text-2xl uppercase tracking-tight">{f.fighter_name}</h3>
                          <div className="flex items-center gap-1.5 text-sm text-zinc-400 mt-1"><Dumbbell size={13} className="text-volt" />{f.gym_name}</div>
                          <div className="flex items-center gap-1.5 text-xs text-zinc-500 mt-1"><MapPin size={12} />{f.city} · {f.weight}{f.weight_unit}</div>
                          <div className="mt-3 text-xs uppercase tracking-widest text-volt flex items-center gap-1">Build camp →</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
        )}
      </main>

      <AddFighterModal open={addOpen} onOpenChange={setAddOpen} onInvited={refresh} />
      <TrainerProfilePanel open={profileOpen} onOpenChange={setProfileOpen} />
    </div>
  );
}
